import { generateClient } from "aws-amplify/api";
import axios from "axios";
import { io, Socket } from "socket.io-client";
import {
  listUsers as gqlListUsers,
  getUser as gqlGetUser,
  listInterviews,
  listJobRoles,
  listQuestions as gqlListQuestions,
  listAttributeQuestions as gqlListAttributeQuestions,
  listAttributeDefinitions as gqlListAttributeDefinitions,
  listQuestionAttributeAnalyses as gqlListQuestionAttributeAnalyses,
  listReviews as gqlListReviews,
  listValueAttributes as gqlListValueAttributes,
} from "../graphql/queries";
import {
  createCompany as gqlCreateCompany,
  createUser as gqlCreateUser,
  updateUser as gqlUpdateUser,
  updateCompany as gqlUpdateCompany,
  createJobRole as gqlCreateJobRole,
  updateJobRole as gqlUpdateJobRole,
  deleteJobRole as gqlDeleteJobRole,
  createValue as gqlCreateValue,
  updateValue as gqlUpdateValue,
  createOrganization as gqlCreateOrganization,
  deleteAttribute as gqlDeleteAttribute,
  createQuestion as gqreateQuestion,
  createValueAttribute as gqlCreateValueAttribute,
  updateValueAttribute as gqlUpdateValueAttribute,
  createAttributeDefinitions as gqlCreateAttributeDefinitions,
} from "../graphql/mutations";
import { v4 as uuid } from "uuid";
import {
  getCompanyData,
  getInterviewBuilderData,
  getInterviewCount as gqlGetInterviewCount,
  listCompaniesWithUsers as gqlListCompanies,
  updateInterviewBuilderData,
  getOrganization as gqlGetOrganizationById,
  updateOrganization as gqlUpdateOrganization,
  getAnalysisById as gqlGetAnalysis,
  getMeetingsByInterviewId as gqlGetMeetingsByInterviewId,
  listAttributes as gqlListAttributes,
  createAttribute as gqlCreateAttribute,
  updateAttribute as gqlUpdateAttribute,
  getMeetingFilesForInterview as gqlMeetingFilesForInterview,
  getValue as gqlGetValue,
  listValues as gqlListValues,
  listCompaniesBuilder as gqlListCompaniesBuilder,
  listOrganizationsValues as gqlListOrganizationsValues
} from "../graphql/custom";

const rootServerApiUrl = import.meta.env.VITE_API_URL;
const {VITE_API_GATEWAY_URL, VITE_TEST_PROMPTS_URL} = import.meta.env;

// Routed through here (rather than reading import.meta.env directly in the
// caller) so callers stay importable under jest — this module is swapped
// for src/__mocks__/api.js in tests, but a bare `import.meta.env` reference
// in the caller's own file would still fail to parse under CommonJS/Jest
// regardless of mocking, since that's a module-format issue, not a value one.
export function getNextQuestionUrl() {
  return import.meta.env.VITE_ML_API_URL + "/next_question";
}

export async function getToken(interviewId, username) {
  const sessionId = await createSession(interviewId);
  return await createToken(sessionId, username);
}

// api utils

const urlConcat = (path, base) => {
    if (!path || !path.length) {
	return base;
    }
    
    let url = base;
    if (url.endsWith("/")) {
	url = base.substr(0, base.length - 1);
    }
    if (!path.startsWith("/")) {
	url += "/";
    }
    url += path;
    
    return url;
}

async function get(path, params) {
  const url = urlConcat(path, rootServerApiUrl);
  // console.log(`HTTP GET ${url}`);
  const { data } = await axios.get(url, params);
  return data;
}

async function post(path, payload) {
  const url = urlConcat(path, rootServerApiUrl);
  // console.log(`HTTP POST ${url}`);
  const { data } = await axios.post(url, payload);
  return data;
}

async function postAPI(path, payload) {
  const url = urlConcat(path, VITE_API_GATEWAY_URL);
  // console.log(`HTTP POST ${url}`);
  const { data } = await axios.post(url, payload, { timeout: 600000 });
  return data;
}

async function postFormData(path, payload) {
  const url = urlConcat(path, rootServerApiUrl);
  console.log(`HTTP POST ${url}`);
  const { data } = await axios.post(url, payload, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return data;
}

async function put(path, payload) {
  const url = urlConcat(path, rootServerApiUrl);
  console.log(`HTTP PUT ${url}`);
  const { data } = await axios.put(url, payload);
  return data;
}

// socket utils

// One shared connection to the ML API. Its lifecycle is owned by useAudioStream,
// which opens it for an interview and closes it when the interview ends.
// Everything else (the Interview / Intervention listeners) is a passive
// subscriber and must never bring a connection into existence.
let socket: Socket | null = null;

// Subscribers need to know when a socket appears or goes away, because they
// mount before the owner opens one. React flushes child effects before parent
// effects, and openSocket() sits behind an await, so a subscriber that only
// reads getSocket() once will always read null and never retry. This store lets
// them re-read on every change instead of relying on an incidental re-render.
const socketWatchers = new Set<() => void>();

const notifySocketWatchers = () => {
  socketWatchers.forEach((notify) => notify());
};

/** Subscribe to socket open/close. Returns an unsubscribe fn. */
export function subscribeToSocket(onChange: () => void): () => void {
  socketWatchers.add(onChange);
  return () => {
    socketWatchers.delete(onChange);
  };
}

/**
 * Open (or reuse) the shared socket. OWNER ONLY — useAudioStream.
 *
 * Dials a fresh connection when there is no live one. A socket that has been
 * explicitly closed can never be reused: socket.io drops emits on it silently,
 * so init_session would not land, the ML API would hold no session for the
 * interview, and nothing would trigger the final transcription.
 *
 * `active` is the correct liveness test — it stays true while a fresh socket is
 * still dialling and across automatic reconnects, and only goes false after an
 * explicit close. Testing `connected` would spawn a duplicate on every call made
 * before the handshake completes.
 */
export function openSocket(): Socket {
  if (socket && socket.active) return socket;

  const url = import.meta.env.VITE_ML_API_URL;
  const path = "/ws/socket.io";
  socket = io(url, { path });
  notifySocketWatchers();
  return socket;
}

/**
 * The shared socket if one is open, otherwise null. SUBSCRIBERS ONLY.
 *
 * Deliberately never dials. Subscribers re-run their effects whenever their
 * props change — including after the interview has ended and the socket has
 * been closed. Dialling from here leaves a connection that never receives
 * init_session, that no one owns, and that nothing ever closes.
 */
export function getSocket(): Socket | null {
  return socket && socket.active ? socket : null;
}

/**
 * Remove the shared socket from the cache WITHOUT closing it, and hand it back.
 *
 * Lets a caller flush pending work on the old connection while the next
 * openSocket() already dials a fresh one — so a deferred close can never take
 * down a newer session, and subscribers see "no socket" immediately.
 */
export function detachSocket(): Socket | null {
  const detached = socket;
  if (!detached) return null;
  socket = null;
  notifySocketWatchers();
  return detached;
}

/** Close the shared socket and drop it. Safe to call when none is open. */
export function closeSocket(): void {
  if (!socket) return;
  socket.close();
  socket = null;
  notifySocketWatchers();
}

function createSession(interviewId) {
  return post("sessions", { customSessionId: interviewId });
}

function createToken(sessionId, username) {
  const path = "sessions/" + sessionId + "/connections";
  return post(path, { username });
}

export async function listConnections(sessionId) {
  return get("sessions/" + sessionId + "/list-connections");
}

export function closeSession(sessionId) {
  return post("sessions/close-session", { sessionId });
}

export async function isJobRoleAccessEnabled(userEmail) {
  try {
    return get("user/jobrolefeature", {
      params: {
        email: userEmail,
      },
    });
  }
  catch(error) {
    console.error(error.message)
  }
}

export async function isJobRoleMigrationEnabled(userEmail) {
  return get("user/rolemigration", {
    params: {
      email: userEmail,
    },
  });
}

export async function isOrganizationProfileEnabled(userEmail) {
  return get("user/organizationprofile", {
    params: {
      email: userEmail,
    },
  });
}

export function startRecording(sessionId, stage) {
  return post("recording/start", {
    hasAudio: true,
    hasVideo: false,
    sessionId,
    stage,
  });
}

export function stopRecording(recordingId) {
  console.log("recording id: ", recordingId);
  return post("recording/stop", { recordingId });
}

export async function GraphQL(params) {
  // console.log(`Executing "${params.query.substring(0, Math.min(params.query.length - 1, 30))}"`, params.variables);
  const {
    query,
    variables = {},
    authMode = "userPool",
  } = params;
  
  const client = generateClient();
  try {
    const res = await client.graphql({
      query,
      variables,
      authMode
    });
    return res.data;
  } catch (e) {
    // Amplify throws whenever the GraphQL response contains an `errors` array,
    // even on partial success where usable `data` is also returned (common with
    // AppSync list queries when a single item fails field resolution). Treat
    // that as non-fatal: log the field-level errors and return the data we did
    // get, so one bad record doesn't break the whole page/query.
    if (e?.data) {
      console.warn("GraphQL partial errors:", e.errors ?? e);
      return e.data;
    }
    console.error("GraphQL", e);
    console.log("Error name", e.name);
    throw e;
  }
}

export function createCognitoUser(params) {
  // cognito create user
  const { username, password, company } = params;
  return post(`user/signup`, {
    username,
    password,
    companyID: company,
  });
}

export async function addUserToDB(params) {
  // db create user
  const {
    id,
    firstName,
    lastName,
    group,
    companyID,
    title,
    email,
    phone,
    status,
    role,
  } = params;

  const { createUser } = await GraphQL({
    query: gqlCreateUser,
    variables: {
      input: {
        id,
        firstName,
        lastName,
        companyID,
        group,
        title,
        email,
        phone,
        status,
        role,
        hasLoggedIn: false,
      },
    },
  });

  return createUser;
}

export function onboardUser(params) {
  const { username, password } = params;
  return post(`user/onboarding`, {
    username,
    password,
  });
}

export async function createCompany(params) {
  const companyId = uuid();
  const { interviewInfo, company } = params;
  const { createCompany } = await GraphQL({
    query: gqlCreateCompany,
    variables: {
      input: {
        id: companyId,
        group: companyId,
        company,
        hasLoggedIn: false,
        status: "Active",
        interviewInfo: JSON.stringify(interviewInfo),
      },
    },
  });

  // company cognito group creation
  const data = await post(`customer/create`, { company, id: companyId });
  console.log("created cognito group for company: ", data);

  return createCompany;
}

export async function updateCompany(params) {
  const variables = { input: params };
  const { updateCompany } = await GraphQL({
    query: gqlUpdateCompany,
    variables,
  });

  // update customer within cognito
  await put(`customer/update`, params);

  return updateCompany;
}

export async function listCompanies() {
  const { listCompanies } = await GraphQL({
    query: gqlListCompanies,
    variables: { filter: { status: { eq: "Active" } } },
  });

  return listCompanies;
}

export async function listUsersByCompanyId(companyID) {
  const { listUsers } = await GraphQL({
    query: gqlListUsers,
    variables: { filter: { status: { eq: "Active" },  companyID: { eq: companyID } } },
  });
  return listUsers;
}

export async function listUsers() {
  const { listUsers } = await GraphQL({
    query: gqlListUsers,
    variables: { filter: { status: { eq: "Active" } } },
  });
  return listUsers;
}

export async function userArchived(email) {
  const { listUsers } = await GraphQL({
    query: gqlListUsers,
    variables: { filter: { status: { eq: "Archived" } } },
  });
  let res = listUsers?.items.map((user) => user?.email).includes(email);
  return res;
}

export async function listArchivedUsers() {
  const { listUsers } = await GraphQL({
    query: gqlListUsers,
    variables: { filter: { status: { eq: "Archived" } } },
  });
  return listUsers;
}

export async function getUser(sub) {
  const { getUser } = await GraphQL({
    query: gqlGetUser,
    variables: { id: sub },
  });
  return getUser;
}

export async function listOwnerUsers() {
  const { listUsers } = await GraphQL({
    query: gqlListUsers,
    variables: {
      filter: { role: { eq: "owner" } },
    },
  });
  return listUsers;
}

export async function getInterviewCount(companyId) {
  let totalCount = 0;
  let nextToken = null;
  do {
    const query = listInterviews;
    const {
      listInterviews: { items, nextToken: newNextToken },
    } = await GraphQL({
      query,
      variables: {
        limit: 1000,
        nextToken,
        filter: { companyID: { eq: companyId } },
      },
    });
    totalCount += items.length;
    nextToken = newNextToken;
  } while (nextToken);

  return totalCount;
}

export async function listInterviewsDashboard(companyId) {
  let allInterviews = [];
  let nextToken = null;
  
  do {
    const variables = {
      limit: 2000,
      nextToken,
      ...(companyId && {
        filter: {
          companyID: { eq: companyId }
        }
      })
    };
    
    const result = await GraphQL({
      query: listInterviews,
      variables
    });
    
    allInterviews = allInterviews.concat(result.listInterviews.items);
    nextToken = result.listInterviews.nextToken;
  } while (nextToken);
  
  return { listInterviews: { items: allInterviews } };
}

export async function getInterviewCountAggregate(companyId) {
  let totalCount = 0;
  let nextToken = null;
  do {
    const {
      searchInterviews: { total, nextToken: newNextToken },
    } = await GraphQL({
      query: gqlGetInterviewCount,
      variables: {
        nextToken,
        // filter: { companyID: { eq: companyId } },
        limit: 1000,
      },
    });
    console.log("response: ", total, nextToken);
    totalCount += total;
    // nextToken = newNextToken;
  } while (nextToken);

  return totalCount;
}

export async function getUserCount(companyId) {
  const {
    getCompany: {
      users: { items },
    },
  } = await GraphQL({
    query: getCompanyData,
    variables: { id: companyId, userStatus: "Active" },
  });
  return items.length;
}

export async function getCompany(cId) {
  const { getCompany } = await GraphQL({
    query: getCompanyData,
    variables: { id: cId, userStatus: "Active" },
  });
  return getCompany;
}

export async function getCompanyInterviewBuilderData(cId) {
  const { getCompany } = await GraphQL({
    query: getInterviewBuilderData,
    variables: { id: cId },
  });
  return getCompany;
}

export async function updateCompanyInterviewBuilderData(
  id,
  interviewBuilderData,
) {
  const variables = {
    input: {
      id,
      interviewBuilderData: JSON.stringify(interviewBuilderData, null, 3),
    },
  };
  const graphQlArgs = {
    query: updateInterviewBuilderData,
    variables,
  };
  const {
    updateCompany: { updatedAt },
  } = await GraphQL(graphQlArgs);
  return updatedAt;
}

export async function updateUser(params) {
  const variables = { input: params };
  const { updateUser } = await GraphQL({ query: gqlUpdateUser, variables });

  return updateUser;
}

export async function createJobRole(params) {
  const jobRoleId = uuid();
  const { jobRoleData, companyID, workingSession } = params;

  const variables = {
      input: {
          id: jobRoleId,
          group: companyID,
          companyID: companyID,
          status: "Open",
          jobRoleData: JSON.stringify(jobRoleData),
          workingSession: workingSession,
      },
  };

  try {
    console.log("JobRole", variables)
      const response = await GraphQL({
          query: gqlCreateJobRole,
          variables: variables,
      });

      return response;
  } catch (error) {
      console.error('Error creating job role:', error);
  }
}

export async function migrateJobRole(params) {
  const { id, jobRoleData, companyID, workingSession } = params;

  const variables = {
      input: {
          id: id,
          group: companyID,
          companyID: companyID,
          status: "Open",
          jobRoleData: JSON.stringify(jobRoleData),
          workingSession: workingSession,
      },
  };

  try {
      const response = await GraphQL({
          query: gqlCreateJobRole,
          variables: variables,
      });

      return response;
  } catch (error) {
      console.error('Error migrating job role:', error);
  }
}

export async function updateJobRole(params) {

  const { id, jobRoleData, status } = params;

  const variables = {
    input: {
      id : id,
      status: status,
      jobRoleData: JSON.stringify(jobRoleData),
    },
  };
  const { updateJobRole } = await GraphQL({ query: gqlUpdateJobRole, variables });

  return updateJobRole;
}

export async function updateJobRoleGroup(params) {

  const { id, group } = params;
  const variables = {
    input: {
      id : id,
      group: group,
    },
  };
  const { updateJobRoleGroup } = await GraphQL({ query: gqlUpdateJobRole, variables });

  return updateJobRoleGroup;
}

export async function updateJobRoleStatus(params) {

  const { id, status } = params;

  const variables = {
    input: {
      id : id,
      status: status,
    },
  };
  const { updateJobRole } = await GraphQL({ query: gqlUpdateJobRole, variables });

  return updateJobRole;
}

export async function listJobRolesReqByCompId(compId) {
  const listJobRolesReq = await GraphQL({
    query: listJobRoles,
    variables: {
      filter: {
        companyID: { eq: compId }
      }
    }
  });
 
  return listJobRolesReq;
}

export async function listQuestionsByCompanyId(compId) {
  const listQuestions = await GraphQL({
    query: gqlListQuestions,
    variables: {
      filter: {
        companyID: { eq: compId }
      }
    }
  });
 
  return listQuestions;
}

export async function listReviewsByInterviewIdAndStage(interviewId, stage) {
  const listReviews = await GraphQL({
    query: gqlListReviews,
    variables: {
      filter: {
        interviewId: { eq: interviewId }
      }
    }
  });
  
  const filtered = listReviews?.listReviews?.items?.map(item => {
    try {
      const { author } = item;
      const parsedNotes = JSON?.parse(item?.notes);

      if(parsedNotes?.stage === stage) {
        const { notes, reason, pass} = parsedNotes || {};
        const personId = author?.toLowerCase()?.replaceAll(" ", "-");
        return {
          notes: notes || reason || "",
          reason: reason || notes || "",
          pass,
          personId,
          author,
        };
      }
      return null;
    } catch {
      return false;
    }
  }) || [];

  return filtered;
}

export async function createQuestion(params) {
  const createQuestion = await GraphQL({
    query: gqreateQuestion,
    variables: {
      input: params
    }
  });
  return createQuestion;
}

export async function listJobRolesReq() {
  const listJobRolesReq = await GraphQL({
    query: listJobRoles,
  });
 
  return listJobRolesReq;
}

export async function deleteJobRole(jrId) {
  const deleteJobRole = await GraphQL({
    query: gqlDeleteJobRole,
    variables: {
        input: {
            id: jrId
        }
    }
  });
  return deleteJobRole;
}

export async function extractJobRoleDataFromJobDescription(jobDesc) {
  try {
    const data = await post("rolemanagement/generatejobroledatafromjobdescription", {
      jobDescription: jobDesc,
    });

    return data;

  } catch (error) {
    console.error("Error generating job role data:", error);
    return [];
  }
}

export async function getValueById(valueId) {
  const getValue = await GraphQL({
    query: gqlGetValue,
    variables: { id: valueId },
  });
 
  return getValue?.getValue;
}

export async function listValues() {
  try {
    const listValues = await GraphQL({
      query: gqlListValues,
    });
    return listValues?.listValues;
 
  } catch (error) {
    console.error("Error in list values:", error);
    return [];
  }
}

export async function listValuesPerOrgId(compId, hasAccess) {
  try {
    const listValues = await GraphQL({
      query: gqlListValues,
      variables: {
        filter: {
          organizationID: { eq: compId }
        }
      }
    });
    return listValues?.listValues?.items;
    // const statusValues = hasAccess ? ["published", "created", "updated"] : ["published", "pendingdelete"];
    // if (listValues && listValues.listValues && Array.isArray(listValues.listValues.items)) {
    //   return listValues.listValues.items.filter(item => {
    //     const status = item.status;
    //     if (Array.isArray(status)) {
    //       return status.some(s => statusValues.includes(s));
    //     } else if (typeof status === 'string') {
    //       return statusValues.includes(status);
    //     } else {
    //       console.error("Unexpected status type:", status);
    //       return false;
    //     }
    //   });
    // } else {
    //   console.error("Unexpected response structure:", listValues);
    //   return [];
    // }
  } catch (error) {
    console.error("Error in listValuesPerOrgId:", error);
    return [];
  }
}


//Values
export async function createValue(params) {
  const valueId = uuid();
   const { group, name, description, organizationID, behaviors } = params
   const variables = {
      input : {
        id : valueId,
        group : group,
        name : name,
        description: description,
        weight : 5, //default value
        status : "created",
        companyId : organizationID,
        organizationID : organizationID,
        behaviors : JSON.stringify(behaviors),
      }
   };
   try {
    const response = await GraphQL({
        query: gqlCreateValue,
        variables: variables,
    });

    return response;
    } catch (error) {
        console.error('Error creating value:', error);
    }
}

export async function createAttributeDefinitions(params) {
  const id = uuid();
  const { name, definition, stage } = params
  const variables = {
    input : {
      id,
      name,
      definition,
      stage
    }
  };
  try {
    const response = await GraphQL({
      query: gqlCreateAttributeDefinitions,
      variables: variables,
    });
    return response;
  } catch (error) {
    console.error('Error creating attribute definition:', error);
  }
}

export async function createValueAttribute(params) {
  const id = uuid();
  const { companyId, attributeId, valueId, order, weight } = params
  const variables = {
    input : {
      id,
      valueId,
      group : companyId,
      weight : weight || 5,
      order : order || 0,
      companyId,
      attributeId
    }
  };
  try {
    const response = await GraphQL({
      query: gqlCreateValueAttribute,
      variables: variables,
    });
    return response;
  } catch (error) {
    console.error('Error creating value attribute:', error);
  }
}

export async function updateValueAttribute(params) {
  const { id, companyId, attributeId, valueId, order, weight } = params
  const variables = {
    input : {
      id,
      valueId,
      group : companyId,
      weight : weight || 25,
      order : order || 0,
      companyId,
      attributeId
    }
  };
  try {
    const response = await GraphQL({
      query: gqlUpdateValueAttribute,
      variables: variables,
    });
    return response;
  } catch (error) {
    console.error('Error updating value attribute:', error);
  }
}

export async function listValueAttributes() {  try {
    const listValueAttributes = await GraphQL({
      query: gqlListValueAttributes,
    });
    return listValueAttributes?.listValueAttributes;
 
  } catch (error) {
    console.error("Error in list value attributes:", error);
    return [];
  }
}

export async function updateValue(params) {
   const { id , group, name, description, organizationID, behaviors, weight } = params
   const variables = {
      input : {
        id : id,
        group : group,
        name : name,
        description: description,
        weight : weight || 0,
        organizationID : organizationID,
        behaviors : JSON.stringify(behaviors),
      }
   };
   try {
    const response = await GraphQL({
        query: gqlUpdateValue,
        variables: variables,
    });

    return response;
    } catch (error) {
        console.error('Error updating value:', error);
    }
}

export async function updateValueStatus(params) {
  const { id , status} = params
  const variables = {
     input : {
       id : id,
       status : status,
     }
  };
  try {
   const response = await GraphQL({
       query: gqlUpdateValue,
       variables: variables,
   });

   return response;
   } catch (error) {
       console.error('Error creating value:', error);
   }
}

export async function updateValueWeight(params) {
  const { id , group, weight, organizationID } = params
  const variables = {
     input : {
       id : id,
       weight : weight,
     }
  };
  try {
   const response = await GraphQL({
       query: gqlUpdateValue,
       variables: variables,
   });

   return response;
   } catch (error) {
       console.error('Error creating value:', error);
   }
}

export async function createAttribute(params) {
  const attributeId = uuid();
   const { title, description, companyId, examples, weight, valueAttributesId, questionIds } = params
   const variables = {
      input : {
        id : attributeId,
        group : companyId,
        title : title,
        description: description,
        weight : weight,
        valueAttributesId : valueAttributesId,
        companyID : companyId,
        examples : examples,
      },
      questions : {
        items : {
          questionID : questionIds,
          attributeID : attributeId
        }
      }
   };
   try {
    console.log("variables", variables)
    const response = await GraphQL({
        query: gqlCreateAttribute,
        variables: variables,
    });

    return response;
    } catch (error) {
        console.error('Error creating attribute:', error);
    }
}

export async function updateAttribute(params) {
  const { id, title, description, companyId, examples, weight, valueAttributesId } = params
  const variables = {
    input : {
      id : id,
      group : companyId,
      title : title,
      description: description,
      weight : weight,
      valueAttributesId : valueAttributesId,
      companyID : companyId,
      examples : examples,
    },
 };
  try {
    console.log(variables)
   const response = await GraphQL({
       query: gqlUpdateAttribute,
       variables: variables,
   });

   return response;
   } catch (error) {
       console.error('Error updating attribute:', error);
   }
}

export async function deleteAttribute(attrId) {
  console.log("attrid", attrId)
  const deleteAttribute = await GraphQL({
    query: gqlDeleteAttribute,
    variables: {
        input: {
            id: attrId
        }
    }
  });
  return deleteAttribute;
}

// Behavior
export async function generateExampleBehaviors(value) {
  try {
  
    const data = await get("organization/generatebehaviors", { params: {
      valueTitle: value.title,
      valueDescription : value.description
    }, })

    return data;

  } catch (error) {
    console.error("Error generating behaviors:", error);
    return [];
  }
}

//Attributes
export async function generateAttributes(value) {
  try {
    const currentBehaviors = [];
      JSON.parse(value.behaviors).map((behavior) => {
      const { title, description } = behavior;  
      currentBehaviors.push({ title: title, description: description });  
    });

    const currentValue = {
      title: value.title,
      description : value.description,
      behaviors : currentBehaviors
    }
    const data = await post("organization/generateattributes", {
      value: currentValue,
    });

    return data;

  } catch (error) {
    console.error("Error generating behaviors:", error);
    return [];
  }
}

export async function listAttributesByOrgId(orgId) {
  const listAttributes = await GraphQL({
    query: gqlListAttributes,
    variables: {
      filter: {
        companyID: { eq: orgId }
      }
    }
  });
 
  return listAttributes;
}

export async function listAttributesByValueId(valueAttributesId) {
  const listAttributes = await GraphQL({
    query: gqlListAttributes,
    variables: {
      filter: {
        valueAttributesId: { eq: valueAttributesId }
      }
    }
  });
 
  return listAttributes;
}

export async function listAttributeDefinitions() {
  const listAttributeDefinitions = await GraphQL({
    query: gqlListAttributeDefinitions,
    variables: {
      filter: {
        stage: { eq: "values" }
      }
    }
  });
 
  return listAttributeDefinitions?.listAttributeDefinitions?.items;
}

export async function listAttributeQuestions(valueAttributesId) {
  const listAttributes = await GraphQL({
    query: gqlListAttributes,
    variables: {
      filter: {
        attributeID: { eq: valueAttributesId }
      }
    }
  });
 
  return listAttributes;
}

export async function listCompaniesBuilder() {
  const listCompaniesBuilder = await GraphQL({
    query: gqlListCompaniesBuilder
  })
  return listCompaniesBuilder?.listCompanies
}

export async function listOrganizationsValues() {
  const listOrganizationsValues = await GraphQL({
    query: gqlListOrganizationsValues
  })
  return listOrganizationsValues?.listOrganizations
}
//Organization

export async function getOrganizationById(compId) {
  const getOrganization = await GraphQL({
    query: gqlGetOrganizationById,
    variables: { id: compId }
  });
 
  return getOrganization;
}
export async function createOrganization(params) {

   const { group, name, organizationData, organizationID } = params
   const variables = {
      input : {
        id : organizationID,
        group : group,
        name : name,
        isPublished : false,
        companyID: organizationID,
        organizationData : JSON.stringify(organizationData),
      }
   };
   try {
    const response = await GraphQL({
        query: gqlCreateOrganization,
        variables: variables,
    });

    return response;
    } catch (error) {
        console.error('Error creating organization:', error);
    }
}

export async function updateOrganization(params) {

  const { group, name, organizationData, organizationID, values } = params
  const formattedValues = Array.isArray(values) ? values : [values];
  console.log(formattedValues)
  const variables = {
     input : {
       id : organizationID,
       group : group,
       name : name,
       isPublished : false,
       companyID: organizationID,
       organizationData : JSON.stringify(organizationData),
     },
    //  values : { items : formattedValues}
  };
  try {
   const response = await GraphQL({
       query: gqlUpdateOrganization,
       variables: variables,
   });

   return response;
   } catch (error) {
       console.error('Error updating organization:', error);
   }
}

export async function publishOrganization(params) {

  const { group, name, organizationData, organizationID, values } = params
  const formattedValues = Array.isArray(values) ? values : [values];
  const variables = {
     input : {
       id : organizationID,
       group : group,
       name : name,
       isPublished : true,
       companyID: organizationID,
       organizationData : JSON.stringify(organizationData),
     },
    //  values : formattedValues
  };
  try {
   const response = await GraphQL({
       query: gqlUpdateOrganization,
       variables: variables,
   });

   return response;
   } catch (error) {
       console.error('Error updating organization:', error);
   }
}

export async function getMeetingsByInterviewId(int_id) {
  return await GraphQL({
    query: gqlGetMeetingsByInterviewId,
    variables: { int_id }
  });
}

export async function getAnalysis(id) {
  const analysisData = await GraphQL({
    query: gqlGetAnalysis,
    variables: {id}
  });
 
  return analysisData;
}

export async function postTestPrompts(params) {
  try {
    const data = await postAPI(VITE_TEST_PROMPTS_URL, params);

    return data?.body;
  } catch (error) {
    console.error("Error generating test prompts:", error);
    return [];
  }
}

export async function getStageConclusion(params) {
  try {
    const currentScores = {
      proceed : params.proceed,
      scores : params.scores
    }
    console.log("currentScores", currentScores)
    const data = await post("summary/rationalizeconclusion", {
      requestData: currentScores,
    });

    return data;

  } catch (error) {
    console.error("Error getting stage conclusion:", error);
    return [];
  }
}

export async function listAttributeAnalyses(qAttrAId) {
  try {
    console.log("attrId", qAttrAId)
    const listQuestionAttributeAnalyses = await GraphQL({
      query: gqlListQuestionAttributeAnalyses,
      variables: {
        filter: {
          questionAttributeAnalysisQuestionId: { eq: qAttrAId }
        }
      }
    });
   
    return listQuestionAttributeAnalyses;

  } catch (error) {
    console.error("Error getting Attribute Analysis:", error);
    return [];
  }
}

export async function getMeetingFilesForInterview(int_id) {
  const filesData = await GraphQL({
    query: gqlMeetingFilesForInterview,
    variables: { int_id }
  });
 
  return filesData;
}
